// Apply to job ***********************************************************************************
// pathname: /v1/job/{slug}/apply
// method:   POST
// ************************************************************************************************
import {
  ApiJobApplyOk, ApiJobApplyPayload, GenericV1ErrorsArray, useSwrMutationApi,
} from '@hooks/useSwrApi';
import { SwrToastsProps } from '@type/swr';

// Interface
// ********************************************
interface SwrJobApplyProps {
  slug: string,
  locale?: string,
  successCallback?: () => void,
  errorCallback?: (errors: GenericV1ErrorsArray) => void,
  toastsOptions?: SwrToastsProps,
}

/**
 * @description SWR mutation: POST apply to job. Response status ok: 200.
 *
 * * **slug** - string. The job slug.
 * * **locale** - string. The locale to use for the request. Default: 'ro'.
 * * **successCallback** - function, optional. The callback to call on success.
 * * **errorCallback**: function, optional. The callback to call on fail.
 * * **toastsOptions**: SWR toasts options, optional.
 */
export const useSwrJobApply = (props: SwrJobApplyProps) => {
  // Props destructuring
  // ********************************************
  const {
    slug,
    locale = 'ro',
    successCallback,
    errorCallback,
    toastsOptions,
  } = props;

  // SWR mutation
  // ********************************************
  const {
    data, error, isMutating, reset, trigger,
  } = useSwrMutationApi<ApiJobApplyOk, ApiJobApplyPayload>({
    url: `/v1/job/${slug}/apply`,
    locale,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
    },
    apiOptions: {
      onSuccess: () => {
        // Callback
        if (successCallback) successCallback();
      },
      onError: (errors) => {
        // Callback
        if (errorCallback) errorCallback(errors);
      },
    },
    toastsOptions: {
      showSuccessToast: true,
      showErrorToast: true,
      ...toastsOptions,
    },
  });

  // Return data
  // ********************************************
  return {
    data,
    error,
    isMutating,
    reset,
    trigger,
  };
};
